/**
 * RTWO Ledger & State Engine — Domain Models
 * Phase 5 | Version: 1.0 Ledger Edition
 *
 * Strongly typed domain models for the RTWO ledger and state layer.
 * Defines the internal blockchain state and ledger architecture
 * independent of networking, RPC, wallet, and mainnet deployment.
 *
 * Truthful Data Policy: All runtime values are null until real
 * ledger storage is connected. No fake data is permitted.
 */

// ─────────────────────────────────────────────
// LEDGER PRIMITIVES
// ─────────────────────────────────────────────

export type LedgerHash = string;
export type LedgerAddress = string;
export type LedgerBlockHeight = bigint | null;
export type LedgerTimestamp = number | null;
export type LedgerSupplyAmount = string; // string to preserve precision
export type LedgerBalance = string;
export type LedgerNonce = bigint | null;
export type LedgerSnapshotId = string;
export type LedgerRecoveryPointId = string;
export type LedgerTransitionId = string;

export const LEDGER_ENGINE_VERSION = {
  version: '1.0.0-phaseC2',
  phase: 'Phase C.2 — Persistent Ledger',
  status: 'Storage-Backed Persistence — Hydration From Explicit Writes Only',
} as const;

// ─────────────────────────────────────────────
// LEDGER ENGINE STATUS
// ─────────────────────────────────────────────

export type LedgerEngineStatus =
  'not_initialized' | 'initializing' | 'ready' | 'syncing' | 'operational' | 'error' | 'shutdown';

// ─────────────────────────────────────────────
// STATE TRANSITION LIFECYCLE
// ─────────────────────────────────────────────

/**
 * State Transition Lifecycle:
 * Requested → Validating → Applying → Applied → Committed → Reverted → Failed
 */
export type StateTransitionStatus =
  'requested' | 'validating' | 'applying' | 'applied' | 'committed' | 'reverted' | 'failed';

export interface StateTransitionLifecycleStage {
  status: StateTransitionStatus;
  label: string;
  description: string;
  terminal: boolean;
  requiresStorage: boolean;
}

export const STATE_TRANSITION_LIFECYCLE: StateTransitionLifecycleStage[] = [
  {
    status: 'requested',
    label: 'Requested',
    description: 'A state transition has been requested by the protocol engine or consensus layer.',
    terminal: false,
    requiresStorage: false,
  },
  {
    status: 'validating',
    label: 'Validating',
    description:
      'The transition is being validated against current state root and account balances.',
    terminal: false,
    requiresStorage: true,
  },
  {
    status: 'applying',
    label: 'Applying',
    description: 'Validated transition is being applied to the account state tree.',
    terminal: false,
    requiresStorage: true,
  },
  {
    status: 'applied',
    label: 'Applied',
    description: 'Transition has been applied to the in-memory state. Awaiting commit.',
    terminal: false,
    requiresStorage: true,
  },
  {
    status: 'committed',
    label: 'Committed',
    description:
      'Transition is permanently committed to the ledger storage and state root updated.',
    terminal: true,
    requiresStorage: true,
  },
  {
    status: 'reverted',
    label: 'Reverted',
    description: 'Transition was rolled back due to block rejection or consensus failure.',
    terminal: true,
    requiresStorage: true,
  },
  {
    status: 'failed',
    label: 'Failed',
    description: 'Transition failed due to invalid state, insufficient balance, or storage error.',
    terminal: true,
    requiresStorage: true,
  },
];

// ─────────────────────────────────────────────
// SNAPSHOT LIFECYCLE
// ─────────────────────────────────────────────

/**
 * Snapshot Lifecycle:
 * Pending → Creating → Created → Verified → Archived → Failed
 */
export type SnapshotStatus =
  'pending' | 'creating' | 'created' | 'verified' | 'archived' | 'failed';

export interface SnapshotLifecycleStage {
  status: SnapshotStatus;
  label: string;
  description: string;
  terminal: boolean;
}

export const SNAPSHOT_LIFECYCLE: SnapshotLifecycleStage[] = [
  {
    status: 'pending',
    label: 'Pending',
    description:
      'Snapshot has been scheduled and is waiting for the ledger to reach a stable state.',
    terminal: false,
  },
  {
    status: 'creating',
    label: 'Creating',
    description: 'Snapshot is being created by serializing the current state tree to storage.',
    terminal: false,
  },
  {
    status: 'created',
    label: 'Created',
    description: 'Snapshot has been written to storage and is awaiting integrity verification.',
    terminal: false,
  },
  {
    status: 'verified',
    label: 'Verified',
    description: 'Snapshot integrity has been verified against the state root hash.',
    terminal: false,
  },
  {
    status: 'archived',
    label: 'Archived',
    description: 'Snapshot has been archived for long-term storage and recovery purposes.',
    terminal: true,
  },
  {
    status: 'failed',
    label: 'Failed',
    description: 'Snapshot creation or verification failed due to storage error or hash mismatch.',
    terminal: true,
  },
];

// ─────────────────────────────────────────────
// ACCOUNT TYPE
// ─────────────────────────────────────────────

export type AccountType = 'externally_owned' | 'contract' | 'validator' | 'treasury' | 'system';

export type AccountStatus = 'active' | 'inactive' | 'frozen' | 'pending';

// ─────────────────────────────────────────────
// STORAGE NODE TYPE
// ─────────────────────────────────────────────

export type StorageNodeType = 'branch' | 'leaf' | 'extension' | 'root';

export type StorageNodeStatus = 'active' | 'pruned' | 'pending' | 'dirty';

// ─────────────────────────────────────────────
// STATE TRANSITION TYPE
// ─────────────────────────────────────────────

export type StateTransitionType =
  | 'transfer'
  | 'contract_deploy'
  | 'contract_call'
  | 'stake'
  | 'unstake'
  | 'reward'
  | 'slash'
  | 'genesis';

// ─────────────────────────────────────────────
// RECOVERY POINT STATUS
// ─────────────────────────────────────────────

export type RecoveryPointStatus = 'active' | 'used' | 'expired' | 'failed';

// ─────────────────────────────────────────────
// DOMAIN MODEL 1: RTWOLedgerState
// ─────────────────────────────────────────────

export interface RTWOLedgerState {
  /** Merkle root of the current account state tree */
  stateRoot: LedgerHash | null;

  /** Current block height of the ledger */
  blockHeight: LedgerBlockHeight;

  /** Total number of accounts in the state tree */
  accountCount: number | null;

  /** Total token supply (genesis + minted) */
  totalSupply: LedgerSupplyAmount | null;

  /** Tokens currently in circulation (not locked/staked) */
  circulatingSupply: LedgerSupplyAmount | null;

  /** Tokens locked in contracts or vesting schedules */
  lockedSupply: LedgerSupplyAmount | null;

  /** Tokens staked by validators */
  stakedSupply: LedgerSupplyAmount | null;

  /** Tokens held in the treasury account */
  treasurySupply: LedgerSupplyAmount | null;

  /** Operational status of the ledger engine */
  status: LedgerEngineStatus;

  /** Timestamp of the last ledger state update */
  lastUpdatedAt: LedgerTimestamp;
}

// ─────────────────────────────────────────────
// DOMAIN MODEL 2: RTWOStateTransition
// ─────────────────────────────────────────────

export interface RTWOStateTransition {
  /** Unique identifier for this state transition */
  transitionId: LedgerTransitionId;

  /** State root before this transition was applied */
  fromStateRoot: LedgerHash | null;

  /** State root after this transition was applied */
  toStateRoot: LedgerHash | null;

  /** Block number in which this transition occurred */
  blockNumber: LedgerBlockHeight;

  /** Hash of the transaction that triggered this transition */
  transactionHash: LedgerHash | null;

  /** Type of state change this transition represents */
  transitionType: StateTransitionType;

  /** List of account addresses affected by this transition */
  affectedAccounts: LedgerAddress[];

  /** Current lifecycle status of this transition */
  status: StateTransitionStatus;

  /** Timestamp when this transition was initiated */
  timestamp: LedgerTimestamp;
}

// ─────────────────────────────────────────────
// DOMAIN MODEL 3: RTWOAccountState
// ─────────────────────────────────────────────

export interface RTWOAccountState {
  /** Unique blockchain address of this account */
  address: LedgerAddress;

  /** Current token balance (null until connected to real storage) */
  balance: LedgerBalance | null;

  /** Transaction nonce for replay protection */
  nonce: LedgerNonce;

  /** Root hash of this account's storage trie */
  storageRoot: LedgerHash | null;

  /** Hash of the contract bytecode (null for EOA) */
  codeHash: LedgerHash | null;

  /** Classification of this account */
  accountType: AccountType;

  /** Operational status of this account */
  status: AccountStatus;

  /** Block number of the last state update */
  lastUpdatedBlock: LedgerBlockHeight;

  /** Timestamp of the last state update */
  updatedAt: LedgerTimestamp;
}

// ─────────────────────────────────────────────
// DOMAIN MODEL 4: RTWOStorageNode
// ─────────────────────────────────────────────

export interface RTWOStorageNode {
  /** Hash identifier of this storage node */
  nodeHash: LedgerHash;

  /** Hash of the parent node in the trie */
  parentHash: LedgerHash | null;

  /** Storage key this node represents */
  key: string | null;

  /** Hash of the value stored at this node */
  valueHash: LedgerHash | null;

  /** Depth of this node in the Merkle Patricia Trie */
  depth: number;

  /** Type classification of this node */
  nodeType: StorageNodeType;

  /** Operational status of this node */
  status: StorageNodeStatus;
}

// ─────────────────────────────────────────────
// DOMAIN MODEL 5: RTWOStateSnapshot
// ─────────────────────────────────────────────

export interface RTWOStateSnapshot {
  /** Unique identifier for this snapshot */
  snapshotId: LedgerSnapshotId;

  /** Block number at which this snapshot was taken */
  blockNumber: LedgerBlockHeight;

  /** State root captured in this snapshot */
  stateRoot: LedgerHash | null;

  /** Number of accounts captured in this snapshot */
  accountCount: number | null;

  /** Integrity hash of the snapshot data */
  snapshotHash: LedgerHash | null;

  /** Timestamp when this snapshot was created */
  createdAt: LedgerTimestamp;

  /** Current lifecycle status of this snapshot */
  status: SnapshotStatus;
}

// ─────────────────────────────────────────────
// DOMAIN MODEL 6: RTWOLedgerRecoveryPoint
// ─────────────────────────────────────────────

export interface RTWOLedgerRecoveryPoint {
  /** Unique identifier for this recovery point */
  recoveryPointId: LedgerRecoveryPointId;

  /** Block number this recovery point targets */
  blockNumber: LedgerBlockHeight;

  /** State root at this recovery point */
  stateRoot: LedgerHash | null;

  /** Reference to the snapshot used for this recovery point */
  snapshotId: LedgerSnapshotId | null;

  /** Cryptographic hash of the recovery point data */
  recoveryHash: LedgerHash | null;

  /** Timestamp when this recovery point was created */
  createdAt: LedgerTimestamp;

  /** Operational status of this recovery point */
  status: RecoveryPointStatus;
}

// ─────────────────────────────────────────────
// LEDGER SERVICE IDS
// ─────────────────────────────────────────────

export const LEDGER_SERVICE_IDS = {
  STATE_MANAGEMENT: 'ledger.state_management',
  ACCOUNT_STATE: 'ledger.account_state',
  STATE_TRANSITION: 'ledger.state_transition',
  STATE_ROOT: 'ledger.state_root',
  STORAGE_TREE: 'ledger.storage_tree',
  SNAPSHOT: 'ledger.snapshot',
  RECOVERY_POINT: 'ledger.recovery_point',
  PERSISTENCE: 'ledger.persistence',
  VALIDATION: 'ledger.validation',
  STATE_PROOF: 'ledger.state_proof',
  EVENT_PUBLISHING: 'ledger.event_publishing',
} as const;

// ─────────────────────────────────────────────
// PERSISTENT LEDGER STORAGE CATALOG (Phase C.2)
// ─────────────────────────────────────────────

export interface PersistentLedgerStorageEntity {
  entity: 'blocks' | 'transactions' | 'accounts' | 'snapshots';
  repository: string;
  domainModel: string;
  note: string;
}

export const PERSISTENT_LEDGER_STORAGE_CATALOG: PersistentLedgerStorageEntity[] = [
  {
    entity: 'blocks',
    repository: 'LedgerRepository',
    domainModel: 'RTWOLedgerState',
    note: 'Block-height ledger state — persisted only on explicit commit',
  },
  {
    entity: 'transactions',
    repository: 'StateRepository',
    domainModel: 'RTWOStateTransition',
    note: 'Transaction-linked state transitions — no fabricated transaction history',
  },
  {
    entity: 'accounts',
    repository: 'StateStorageRepository',
    domainModel: 'RTWOAccountState',
    note: 'Account state — empty until explicitly written',
  },
  {
    entity: 'snapshots',
    repository: 'SnapshotRepository',
    domainModel: 'RTWOStateSnapshot',
    note: 'State snapshots — created only from real ingested ledger state',
  },
];

export interface LedgerPersistenceHydrationResult {
  hydrated: boolean;
  storageConnected: boolean;
  blockHeight: LedgerBlockHeight;
  accountCount: number;
  transactionCount: number;
  snapshotCount: number;
  recoveryPointCount: number;
  storageNodeCount: number;
  source: string;
}

// ─────────────────────────────────────────────
// PHASE 5 READINESS CHECKLIST
// ─────────────────────────────────────────────

export interface Phase5ChecklistItem {
  id: string;
  label: string;
  status: 'ready' | 'pending' | 'blocked';
  note: string;
}

export const PHASE5_READINESS_CHECKLIST: Phase5ChecklistItem[] = [
  {
    id: 'module',
    label: 'ledger-state-engine module exists',
    status: 'ready',
    note: 'Created in Phase 5',
  },
  {
    id: 'domain_models',
    label: 'All six domain models implemented',
    status: 'ready',
    note: 'RTWOLedgerState, RTWOStateTransition, RTWOAccountState, RTWOStorageNode, RTWOStateSnapshot, RTWOLedgerRecoveryPoint',
  },
  {
    id: 'service_interfaces',
    label: 'Service interfaces defined',
    status: 'ready',
    note: '10 typed service interfaces with placeholder implementations',
  },
  {
    id: 'transition_lifecycle',
    label: 'State transition lifecycle implemented',
    status: 'ready',
    note: 'Requested → Validating → Applying → Applied → Committed → Reverted → Failed',
  },
  {
    id: 'snapshot_lifecycle',
    label: 'Snapshot lifecycle implemented',
    status: 'ready',
    note: 'Pending → Creating → Created → Verified → Archived → Failed',
  },
  {
    id: 'dashboard',
    label: 'Ledger & State Dashboard exists',
    status: 'ready',
    note: 'Phase 5 dashboard with acceptance checklist',
  },
  {
    id: 'core_integration',
    label: 'Core Engine integration complete',
    status: 'ready',
    note: 'Integrated with R3, R4, R6, R7 execution layers',
  },
  {
    id: 'consensus_integration',
    label: 'Consensus Engine integration complete',
    status: 'ready',
    note: 'Phase 4 Consensus Engine connected via orchestrator',
  },
  {
    id: 'truthful_data',
    label: 'No fake ledger data shown',
    status: 'ready',
    note: 'Truthful Data Policy enforced throughout',
  },
  {
    id: 'real_storage',
    label: 'Storage provider connected to Ledger Engine',
    status: 'ready',
    note: 'Operational RocksDB/in-memory storage bridged via StorageProviderRegistry',
  },
  {
    id: 'persistent_ledger',
    label: 'Persistent ledger hydration and recovery',
    status: 'ready',
    note: 'Phase C.2 — bootstrap from storage on initialize; no fabricated genesis data',
  },
  {
    id: 'p2p_ready',
    label: 'Ready for P2P Network Phase',
    status: 'pending',
    note: 'Ledger architecture complete — awaiting Phase 6',
  },
];

// ─────────────────────────────────────────────
// EMPTY STATE FACTORIES
// ─────────────────────────────────────────────

export function createEmptyLedgerState(): RTWOLedgerState {
  return {
    stateRoot: null,
    blockHeight: null,
    accountCount: null,
    totalSupply: null,
    circulatingSupply: null,
    lockedSupply: null,
    stakedSupply: null,
    treasurySupply: null,
    status: 'not_initialized',
    lastUpdatedAt: null,
  };
}

export function createEmptyAccountState(address: LedgerAddress = ''): RTWOAccountState {
  return {
    address,
    balance: null,
    nonce: null,
    storageRoot: null,
    codeHash: null,
    accountType: 'externally_owned',
    status: 'pending',
    lastUpdatedBlock: null,
    updatedAt: null,
  };
}

export function createEmptySnapshot(): RTWOStateSnapshot {
  return {
    snapshotId: '',
    blockNumber: null,
    stateRoot: null,
    accountCount: null,
    snapshotHash: null,
    createdAt: null,
    status: 'pending',
  };
}

export function createEmptyRecoveryPoint(): RTWOLedgerRecoveryPoint {
  return {
    recoveryPointId: '',
    blockNumber: null,
    stateRoot: null,
    snapshotId: null,
    recoveryHash: null,
    createdAt: null,
    status: 'active',
  };
}
